import React, { useState, useRef, useCallback, useEffect } from "react";
import env from "@beam-australia/react-env";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { SelectButton } from "primereact/selectbutton";
import { useTranslation } from "react-i18next";
import { useDebounce } from "../../../../hooks/useDebounce";

import useApi from "../../../../hooks/useApi";
import useCRUDService from "../../../../hooks/useCRUDService";
import ApplicationsForm from "./applicationsForm";
import Layout from "../../../../layout/layout";
import Header from "../../../../layout/header";
import useSkeleton from "../../../../hooks/useSkeleton";
import helperFormats from "../../../../utils/helperFormats";

import { useAtom } from "jotai";
import { loadingState } from "../../../../states/loadingState";

const Applications = () => {
  const APPLICATION_STATE_PENDING = "not_answered";
  const APPLICATION_STATE_APPROVED = "approved";
  const APPLICATION_STATE_REJECTED = "rejected";

  const [applicationStateFilter, setApplicationStateFilter] = useState(
    APPLICATION_STATE_PENDING
  );

  const s = useSkeleton("commons");
  const api = useApi();
  const { t } = useTranslation();
  const CRUDService = useCRUDService("applications");

  const [entities, setEntities] = useState(null);
  const [formDialog, setFormDialog] = useState(false);

  const [isLoading, setIsLoading] = useAtom(loadingState);

  const [filters, setFilters] = useState({
    totalRecords: 0,
    first: 0,
    rows: 100,
    sortField: "createdDate",
    sortOrder: "DESC",
    globalFilter: "",
  });
  const { totalRecords, first, rows, sortField, sortOrder, globalFilter } =
    filters;

  const searchInput = useRef(null);

  // Inicializamos el debounce para la búsqueda
  const debouncedSearchTerm = useDebounce(globalFilter, 2000);

  const toast = useRef(null);
  const dt = useRef(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const columns = JSON.stringify([
        {
          name: "type",
          type: "string",
        },
        {
          name: "beneficiary_DocumentNumber",
          type: "string",
        },
        {
          name: "beneficiary_Name",
          type: "string",
        },
        {
          name: "beneficiary_LastName",
          type: "string",
        },
        {
          name: "beneficiary_Phone",
          type: "string",
        },
        {
          name: "amount",
          type: "decimal",
        },
        {
          name: "installmentsNumber",
          type: "number",
        },
        {
          name: "installmentsAmount",
          type: "decimal",
        },
        {
          name: "createdDate",
          type: "date",
        },
      ]);

      const response = await api.get(
        `${env("API_URL")}/applications/${applicationStateFilter}/paged`,
        {
          params: {
            columns,
            page: first,
            pageSize: rows,
            sort: sortField ?? "beneficiary_LastName",
            order: sortOrder >= 0 ? "ASC" : "DESC",
            globalFilter: debouncedSearchTerm,
          },
        }
      );

      console.log("API Response:", response);

      setFilters((prevFilters) => ({
        ...prevFilters,
        totalRecords: response.data.totalRows,
      }));
      setEntities(response.data.rows);
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: t("errors.general"),
        life: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    first,
    rows,
    sortField,
    sortOrder,
    debouncedSearchTerm,
    CRUDService,
    applicationStateFilter,
    setFilters,
    setIsLoading,
  ]);

  useEffect(() => {
    fetchData();
  }, [
    first,
    rows,
    sortField,
    sortOrder,
    debouncedSearchTerm,
    applicationStateFilter,
  ]);

  const handlePageChange = (event) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      first: event.first,
      rows: event.rows,
    }));
  };

  const handleSortChange = (event) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      sortField: event.sortField,
      sortOrder: event.sortOrder,
    }));
  };

  const handleGlobalFilterChange = (e) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      globalFilter: e.target.value,
    }));
  };

  const openFormDialog = async (formMode, selectedApplications) => {
    setFormSettings((prev) => ({
      ...prev,
      formMode: formMode,
      entityId: selectedApplications?.id,
      hideDialog: hideDialog,
      toast: toast,
      CRUDService: CRUDService,
    }));

    setFormDialog(true);
  };

  const hideDialog = (action) => {
    if (action !== "CANCEL") {
      setFormDialog(false);
      // Restablece todos los filtros a sus valores predeterminados
      setFilters({
        totalRecords,
        first,
        rows,
        sortField,
        sortOrder,
        globalFilter,
      });

      // Luego, realiza una nueva búsqueda con los filtros restablecidos
      fetchData();
    }

    setFormDialog(false);
  };

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const [formSettings, setFormSettings] = useState({
    entityId: -1,
    formMode: "",
    hideDialog: hideDialog,
    toast: toast,
    CRUDService: CRUDService,
  });

  const applicationStateOptions = [
    {
      state: APPLICATION_STATE_PENDING,
      label: t("entities.applications.states.pending"),
    },
    {
      state: APPLICATION_STATE_APPROVED,
      label: t("entities.applications.states.approved"),
    },
    {
      state: APPLICATION_STATE_REJECTED,
      label: t("entities.applications.states.rejected"),
    },
  ];

  const actions = (
    <div className="flex justify-content-end">
      <SelectButton
        options={applicationStateOptions || []}
        optionLabel="label"
        optionValue="state"
        className="applicationStates"
        value={applicationStateFilter}
        onChange={(e) => {
          if (e.value) setApplicationStateFilter(e.value);
        }}
      />
      <Button
        label={t("DataTable.New")}
        icon="pi pi-plus"
        className="p-button-primary ml-5"
        onClick={() => openFormDialog("CREATE")}
      />
    </div>
  );

  const actionBodyTemplate = (rowData) => {
    const isAnswered = helperFormats.isDate(rowData.answerDate);
    const formMode = isAnswered ? "VIEW" : "UPDATE";
    const icon = isAnswered ? "pi pi-eye" : "pi pi-pencil";
    const buttonClass = isAnswered
      ? "p-button-rounded p-button-info mr-2"
      : "p-button-rounded p-button-success mr-2";

    return (
      <React.Fragment>
        <Button
          icon={icon}
          className={buttonClass}
          onClick={() => openFormDialog(formMode, rowData)}
        />
      </React.Fragment>
    );
  };

  const header = (
    <div className="table-header">
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          ref={searchInput}
          type="search"
          onInput={handleGlobalFilterChange}
          placeholder={`${t("DataTable.Search")}...`}
        />
      </span>
      <Button
        outlined
        label={t("DataTable.Export")}
        icon="pi pi-upload"
        className="p-button-secondary"
        onClick={exportCSV}
        style={{
          marginLeft: "20px",
        }}
      />
    </div>
  );

  const getDialogTitle = () => {
    let action = "Add";

    if (formSettings.formMode == "DELETE") {
      action = "Delete";
    } else if (formSettings.formMode == "UPDATE") {
      action = "Edit";
    } else if (formSettings.formMode == "VIEW") {
      action = "View";
    }

    return `${t(action)} ${t("entities.applications.singular")}`;
  };

  const getDialogHeader = () => {
    if (formSettings.formMode === "DELETE") {
      return getDialogTitle();
    }

    return (
      <div className="abm-form-hero">
        <h2 className="abm-form-hero-title">{getDialogTitle()}</h2>
        <p className="abm-form-hero-subtitle">
          Complete la información para gestionar el registro.
        </p>
      </div>
    );
  };

  return (
    <Layout
      header={
        <Header
          icon={<i className="pi pi-file-edit"></i>}
          title={t("entities.applications.plural")}
          actions={actions}
        />
      }
    >
      <Toast ref={toast} />
      <div className="table-container w-full">
        <DataTable
          onPage={handlePageChange}
          onSort={handleSortChange}
          sortField={filters.sortField}
          sortOrder={filters.sortOrder}
          ref={dt}
          value={entities}
          lazy
          paginator
          first={filters.first}
          rows={filters.rows}
          totalRecords={filters.totalRecords}
          columnResizeMode="fit"
          showGridlines
          dataKey="id"
          rowsPerPageOptions={[10, 25, 100, 1000]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate={`${t(
            "DataTable.currentPageReportTemplate"
          )} ${t("Applications")}`}
          globalFilter={globalFilter}
          header={header}
          className="flex flex-column w-full"
          emptyMessage={t("DataTable.emptyMessage")}
        >
          <Column
            body={(rowData) => {
              return rowData.type.length === 0 || rowData.type === "NEW_CREDIT" ?
                t("entities.applications.types.NEW_CREDIT") :
                t("entities.applications.types.RENEWAL");
            }}
            header={t("entities.applications.type")}
            sortable
            bodyStyle={{ textAlign: "center" }}
          ></Column>
          <Column
            field="beneficiary_DocumentNumber"
            header={t("entities.applications.beneficiary_DocumentNumber")}
            sortable
            bodyStyle={{ textAlign: "center" }}
          ></Column>
          <Column
            field="beneficiary_Name"
            header={t("entities.applications.beneficiary_Name")}
            sortable
            bodyStyle={{ textAlign: "left" }}
          ></Column>
          <Column
            field="beneficiary_LastName"
            header={t("entities.applications.beneficiary_LastName")}
            sortable
            bodyStyle={{ textAlign: "left" }}
          ></Column>
          <Column
            field="beneficiary_Phone"
            header={t("entities.applications.beneficiary_Phone")}
            sortable
            bodyStyle={{ textAlign: "center" }}
          ></Column>
          <Column
            field="amount"
            header={t("entities.applications.amount")}
            body={(rowData) => helperFormats.formatDecimal(rowData.amount)}
            sortable
            bodyStyle={{ textAlign: "right" }}
          ></Column>
          <Column
            field="installmentsNumber"
            header={t("entities.applications.installmentsNumber")}
            sortable
            bodyStyle={{ textAlign: "center" }}
          ></Column>
          <Column
            field="installmentsAmount"
            header={t("entities.applications.installmentsAmount")}
            body={(rowData) => {
              return helperFormats.formatDecimal(rowData.installmentsAmount);
            }}
            sortable
            bodyStyle={{ textAlign: "right" }}
          ></Column>
          <Column
            field="createdDate"
            header={t("entities.applications.createdDate")}
            sortable
            body={(rowData) =>
              helperFormats.formatDate(new Date(rowData.createdDate))
            }
            bodyStyle={{ textAlign: "center" }}
          ></Column>
          <Column
            body={actionBodyTemplate}
            exportable={false}
            style={{ width: "8rem", textAlign: "center" }}
            bodyStyle={{ textAlign: "center" }}
          ></Column>
        </DataTable>
        <React.Fragment>
          <Dialog
            visible={formDialog}
            header={getDialogHeader()}
            modal
            className="flex w-9"
            onHide={hideDialog}
          >
            <ApplicationsForm settings={formSettings} />
          </Dialog>
        </React.Fragment>
      </div>
    </Layout>
  );
};

export default Applications;
